/**
 * LegalGateModal — the first thing every visitor sees.
 *
 * A full-screen, blocking CLICKWRAP gate: the visitor cannot reach the site
 * until they actively tick the boxes and click "I Agree & Enter". Clickwrap
 * (active assent) is far more enforceable than a passive banner.
 *
 * On acceptance we do two things:
 *   1. Write a versioned flag to localStorage so the gate stops re-appearing for
 *      this browser.
 *   2. Best-effort POST to /api/legal/consent so there's a server-side record
 *      (version + IP + user-agent + timestamp) — the part that actually holds up
 *      as evidence. A failed POST never blocks entry.
 *
 * IMPORTANT — bump LEGAL_VERSION whenever the wording below changes. Returning
 * visitors whose stored version differs will be re-prompted to accept the new
 * terms, and the new version string is what gets logged.
 *
 * NOT LEGAL ADVICE: this wording is a reasonable, defensible draft, not a
 * lawyer-reviewed contract. A limitation-of-liability clause cannot waive
 * gross negligence, willful misconduct, a defective product you shipped, or
 * rights that applicable consumer-protection law makes non-waivable, and it
 * does not bind third parties who never visited the site. Have counsel review
 * and localize the bracketed governing-law line before relying on this.
 */
import { useEffect, useState } from 'react';
import api from '../utils/api';

export const LEGAL_VERSION = '2026-05-30';
const STORAGE_KEY = 'truetide-legal-consent';

const CLAUSE_RESEARCH =
  'I am at least 21 and a qualified researcher or laboratory professional. I am ' +
  'purchasing solely for in-vitro / laboratory research use, NOT for human or ' +
  'animal consumption, and NOT for any therapeutic, diagnostic, or personal use.';

const CLAUSE_LIABILITY =
  'I have read and agree to the Assumption of Risk & Limitation of Liability below. ' +
  'I assume all risk arising from my own handling, storage, transport, and use of ' +
  'the products, and I release and will indemnify Aloha Amino from claims to the ' +
  'fullest extent permitted by applicable law.';

/** Read the stored consent; returns true only if it matches the CURRENT version. */
function hasValidConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed && parsed.version === LEGAL_VERSION;
  } catch {
    return false;
  }
}

export default function LegalGateModal() {
  const [open, setOpen] = useState(() => !hasValidConsent());
  const [agreeResearch, setAgreeResearch] = useState(false);
  const [agreeLiability, setAgreeLiability] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Lock background scroll while the gate is up.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const bothChecked = agreeResearch && agreeLiability;

  const handleAccept = async () => {
    if (!bothChecked || submitting) return;
    setSubmitting(true);

    const acceptedAt = new Date().toISOString();
    // Persist locally FIRST so the gate never re-traps a visitor even if the
    // network call fails.
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: LEGAL_VERSION, acceptedAt }));
    } catch { /* private mode / storage disabled — gate may re-show, that's ok */ }

    // Best-effort server-side evidentiary record. Don't block entry on failure.
    try {
      await api.post('/legal/consent', {
        termsVersion: LEGAL_VERSION,
        scope: 'site-entry-gate',
        acceptedClauses: [CLAUSE_RESEARCH, CLAUSE_LIABILITY],
      });
    } catch { /* logged server-side; localStorage flag already set */ }

    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-gate-title"
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="bg-tea-700 px-6 py-5 text-white sm:px-8">
          <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-tea-300">
            Required Legal Notice
          </p>
          <h2 id="legal-gate-title" className="text-2xl font-extrabold tracking-tight">
            Read Before You Enter
          </h2>
        </div>

        {declined ? (
          /* Declined state — entry is not possible without agreement */
          <div className="flex flex-1 flex-col items-center justify-center px-8 py-16 text-center">
            <span className="mb-4 text-4xl" aria-hidden="true">🚫</span>
            <h3 className="mb-2 text-lg font-bold text-gray-900">Access requires agreement</h3>
            <p className="mb-8 max-w-md text-sm leading-relaxed text-gray-600">
              You must accept the research-use and liability terms to use this site. If you do
              not agree, please close this tab.
            </p>
            <button
              type="button"
              onClick={() => setDeclined(false)}
              className="rounded-lg bg-tea-700 px-6 py-2.5 font-semibold text-white transition hover:bg-tea-800"
            >
              Back to terms
            </button>
          </div>
        ) : (
          <>
            {/* Scrollable disclosure body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-8">
              <div className="mb-5 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <span className="shrink-0 text-xl" aria-hidden="true">⚠️</span>
                <p className="text-sm leading-relaxed text-amber-900">
                  <strong>FOR LABORATORY RESEARCH USE ONLY.</strong> Aloha Amino products are not
                  drugs, foods, cosmetics, supplements, or medical devices, and have not been
                  approved by any regulatory body for human or veterinary use. They are sold solely
                  for in-vitro / laboratory research and are <strong>not for human or animal
                  consumption</strong>.
                </p>
              </div>

              <h3 className="mb-2 text-base font-bold text-gray-900">
                Assumption of Risk &amp; Limitation of Liability
              </h3>
              <div className="space-y-3 text-sm leading-relaxed text-gray-600">
                <p>
                  By entering and using this site or purchasing any product, you acknowledge and
                  agree that you assume <strong>all risk</strong> arising from the handling, storage,
                  transport, dilution, disposal, and use of the products once they leave Aloha Amino's
                  custody. You are solely responsible for using the products lawfully, safely, and
                  only for legitimate in-vitro research.
                </p>
                <p>
                  To the fullest extent permitted by applicable law, Aloha Amino, its owners, officers,
                  employees, and affiliates shall <strong>not be liable</strong> for any direct,
                  indirect, incidental, special, consequential, or punitive damages — including
                  personal injury, property damage, or economic loss — arising out of or related to
                  your purchase, handling, misuse, or any use of the products, whether the claim is
                  based in contract, tort, strict liability, or otherwise.
                </p>
                <p>
                  You agree to <strong>release, defend, indemnify, and hold harmless</strong>{' '}
                  Aloha Amino from and against any claim, lawsuit, demand, liability, cost, or expense
                  (including reasonable legal fees) brought by you or by any third party that arises
                  from your breach of these terms, your misrepresentation of your eligibility, or
                  your handling or use of the products.
                </p>
                <p className="text-gray-500">
                  Nothing in this notice excludes or limits liability that cannot be excluded or
                  limited under applicable law (for example, liability for gross negligence,
                  willful misconduct, or any non-waivable consumer-protection or product-safety
                  rights). These terms are governed by the laws of{' '}
                  <span className="font-medium">[your governing jurisdiction]</span> and are in
                  addition to Aloha Amino's{' '}
                  <a href="/terms" target="_blank" rel="noreferrer" className="text-tea-700 underline">Terms of Service</a>,{' '}
                  <a href="/compliance" target="_blank" rel="noreferrer" className="text-tea-700 underline">Research-Use Disclosure</a>, and{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="text-tea-700 underline">Privacy Policy</a>.
                </p>
              </div>

              {/* Checkboxes — clickwrap assent */}
              <div className="mt-6 space-y-4">
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:border-tea-300">
                  <input
                    type="checkbox"
                    checked={agreeResearch}
                    onChange={(e) => setAgreeResearch(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-tea-700"
                  />
                  <span className="text-sm leading-relaxed text-gray-700">{CLAUSE_RESEARCH}</span>
                </label>
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:border-tea-300">
                  <input
                    type="checkbox"
                    checked={agreeLiability}
                    onChange={(e) => setAgreeLiability(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-tea-700"
                  />
                  <span className="text-sm leading-relaxed text-gray-700">{CLAUSE_LIABILITY}</span>
                </label>
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex flex-col-reverse gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
              <button
                type="button"
                onClick={() => setDeclined(true)}
                className="text-sm font-medium text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline"
              >
                I do not agree
              </button>
              <button
                type="button"
                onClick={handleAccept}
                disabled={!bothChecked || submitting}
                className="rounded-lg bg-tea-700 px-6 py-3 font-semibold text-white transition enabled:hover:bg-tea-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {submitting ? 'Saving…' : 'I Agree & Enter'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
